// GitHub utilities specifically for ADA scan data storage
import { formatDate } from './githubUtils';

const GITHUB_CONFIG = {
  token: process.env.REACT_APP_GITHUB_TOKEN,
  owner: process.env.REACT_APP_REPO_OWNER,
  repo: process.env.REACT_APP_REPO_NAME
};

// Generate HTML report from scan data
function generateHTMLReport(scanData) {
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const safeUrl = String(scanData.url || 'Unknown URL');
  const safeTimestamp = formatDate(scanData.timestamp || scanData.savedAt);
  const safeViolations = Array.isArray(scanData.violations) ? scanData.violations : [];
  const score = scanData.score || scanData.analysisResult?.score || 0;
  const totalIssues = scanData.totalIssues || safeViolations.length || 0;

  const getRuleDescription = (ruleId) => {
    const descriptions = {
      'image-alt': 'Add descriptive alt text to images to provide context for screen readers. The alt text should be concise but meaningful.',
      'button-name': 'Ensure all buttons have accessible names through either visible text content or aria-label attributes.',
      'color-contrast': 'Maintain sufficient color contrast (4.5:1 for normal text, 3:1 for large text) between text and background colors.',
      'document-title': 'Provide a descriptive page title that indicates the page\'s purpose or content.',
      'duplicate-id': 'Ensure all IDs in the document are unique to prevent confusion for assistive technologies.',
      'frame-title': 'Add descriptive titles to frames and iframes to help users understand their purpose.',
      'html-has-lang': 'Specify the language of the page using the lang attribute on the html element.',
      'label': 'Associate form controls with labels using either the label element or aria-label attributes.',
      'link-name': 'Ensure all links have accessible names through either visible text, aria-label, or aria-labelledby.',
      'list': 'Use proper list markup (ul, ol, li) for content that represents a list of items.',
      'heading-empty': 'Ensure heading elements are not empty and provide meaningful structure to the page.',
      'form-label': 'Associate form controls with labels to help users understand their purpose.',
    };
    return descriptions[ruleId] || 'Review the element and ensure it follows WCAG 2.1 accessibility guidelines.';
  };

  const template = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Accessibility Report - ${safeUrl}</title>
    <style>
        :root {
            --primary-color: #2563eb;
            --success-color: #10b981;
            --warning-color: #f59e0b;
            --error-color: #ef4444;
            --background: #f8fafc;
            --card-background: #ffffff;
            --text-primary: #1f2937;
            --text-secondary: #6b7280;
            --border-color: #e5e7eb;
            --border-radius: 8px;
        }
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background-color: var(--background);
            color: var(--text-primary);
            line-height: 1.6;
            padding: 20px;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
        }
        .report-header {
            background: var(--card-background);
            padding: 2rem;
            border-radius: var(--border-radius);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            margin-bottom: 2rem;
        }
        .report-header h1 {
            color: var(--primary-color);
            font-size: 2.5rem;
            margin-bottom: 1rem;
            text-align: center;
        }
        .report-header .meta {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1rem;
            margin-top: 1.5rem;
        }
        .meta-item {
            padding: 1rem;
            background: var(--background);
            border-radius: var(--border-radius);
            border-left: 4px solid var(--primary-color);
        }
        .meta-item strong {
            display: block;
            color: var(--text-secondary);
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            margin-bottom: 0.5rem;
        }
        .meta-item span {
            font-size: 1.1rem;
            font-weight: 600;
        }
        .score-container {
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 2rem 0;
        }
        .score-circle {
            width: 180px;
            height: 180px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 3rem;
            font-weight: bold;
            color: white;
            background: ${score >= 90 ? 'var(--success-color)' : score >= 70 ? 'var(--warning-color)' : 'var(--error-color)'};
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }
        .stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin: 2rem 0;
        }
        .stat-card {
            background: var(--card-background);
            padding: 1.5rem;
            border-radius: var(--border-radius);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            text-align: center;
        }
        .stat-card h3 {
            color: var(--text-secondary);
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            margin-bottom: 0.5rem;
        }
        .stat-number {
            font-size: 2.5rem;
            font-weight: bold;
            color: var(--primary-color);
        }
        .violations {
            background: var(--card-background);
            border-radius: var(--border-radius);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }
        .violations-header {
            background: var(--primary-color);
            color: white;
            padding: 1.5rem;
        }
        .violations-header h2 {
            font-size: 1.5rem;
            margin: 0;
        }
        .violation {
            border-bottom: 1px solid var(--border-color);
            padding: 1.5rem;
        }
        .violation:last-child {
            border-bottom: none;
        }
        .violation h3 {
            color: var(--error-color);
            margin-bottom: 1rem;
            font-size: 1.1rem;
        }
        .violation-meta {
            margin-bottom: 1rem;
        }
        .violation-selector {
            background: var(--background);
            padding: 0.75rem;
            border-radius: var(--border-radius);
            margin-bottom: 0.5rem;
            font-family: 'Courier New', monospace;
            font-size: 0.875rem;
            border-left: 3px solid var(--primary-color);
        }
        .violation-html {
            margin-top: 0.5rem;
        }
        .violation-html pre {
            background: #f1f5f9;
            padding: 1rem;
            border-radius: var(--border-radius);
            overflow-x: auto;
            font-size: 0.875rem;
            border: 1px solid var(--border-color);
        }
        .rule-info {
            background: #fef3c7;
            padding: 1rem;
            border-radius: var(--border-radius);
            border-left: 4px solid var(--warning-color);
        }
        .rule-info h4 {
            color: #92400e;
            margin-bottom: 0.5rem;
        }
        .rule-description {
            color: #78350f;
            font-size: 0.95rem;
        }
        .success-message {
            text-align: center;
            padding: 3rem;
            color: var(--success-color);
            font-size: 1.25rem;
            font-weight: 600;
        }
        .footer {
            text-align: center;
            margin-top: 2rem;
            padding: 1rem;
            color: var(--text-secondary);
            font-size: 0.875rem;
        }
        .badge {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 9999px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.025em;
        }
        .badge-success {
            background: #dcfce7;
            color: #166534;
        }
        .badge-warning {
            background: #fef3c7;
            color: #92400e;
        }
        .badge-error {
            background: #fecaca;
            color: #991b1b;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="report-header">
            <h1>🔍 Accessibility Compliance Report</h1>
            <div class="meta">
                <div class="meta-item">
                    <strong>Website URL</strong>
                    <span><a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a></span>
                </div>
                <div class="meta-item">
                    <strong>Analysis Date</strong>
                    <span>${safeTimestamp}</span>
                </div>
                <div class="meta-item">
                    <strong>Tool Used</strong>
                    <span>Nibyor ADA Checker v${scanData.metadata?.version || '1.0'}</span>
                </div>
                <div class="meta-item">
                    <strong>Analysis Type</strong>
                    <span>${scanData.analysisType || 'Standard'}</span>
                </div>
            </div>
        </div>

        <div class="score-container">
            <div class="score-circle">
                ${score}%
            </div>
        </div>

        <div class="stats">
            <div class="stat-card">
                <h3>Accessibility Score</h3>
                <div class="stat-number" style="color: ${score >= 90 ? 'var(--success-color)' : score >= 70 ? 'var(--warning-color)' : 'var(--error-color)'}">
                    ${score}%
                </div>
                <span class="badge ${score >= 90 ? 'badge-success' : score >= 70 ? 'badge-warning' : 'badge-error'}">
                    ${score >= 90 ? 'Excellent' : score >= 70 ? 'Good' : 'Needs Improvement'}
                </span>
            </div>
            <div class="stat-card">
                <h3>Total Issues Found</h3>
                <div class="stat-number">${totalIssues}</div>
                <span class="badge ${totalIssues === 0 ? 'badge-success' : totalIssues <= 5 ? 'badge-warning' : 'badge-error'}">
                    ${totalIssues === 0 ? 'Perfect' : totalIssues <= 5 ? 'Minor Issues' : 'Major Issues'}
                </span>
            </div>
            <div class="stat-card">
                <h3>Compliance Level</h3>
                <div class="stat-number" style="font-size: 1.5rem;">
                    ${score >= 95 ? 'AAA' : score >= 80 ? 'AA' : 'A'}
                </div>
                <span class="badge ${score >= 95 ? 'badge-success' : score >= 80 ? 'badge-warning' : 'badge-error'}">
                    WCAG 2.1 Level ${score >= 95 ? 'AAA' : score >= 80 ? 'AA' : 'A'}
                </span>
            </div>
        </div>

        <div class="violations">
            <div class="violations-header">
                <h2>📋 Detailed Analysis Results</h2>
            </div>
            ${totalIssues === 0 
              ? '<div class="success-message">🎉 Congratulations! No accessibility issues were found. Your website follows WCAG 2.1 guidelines.</div>'
              : safeViolations.map(violation => `
                <div class="violation">
                    <h3>⚠️ ${violation.message || 'Accessibility Issue'}</h3>
                    <div class="violation-meta">
                        ${violation.selector ? `
                            <div class="violation-selector">
                                <strong>Element Selector:</strong> ${violation.selector}
                            </div>
                        ` : ''}
                        ${violation.html ? `
                            <div class="violation-html">
                                <strong>HTML Code:</strong>
                                <pre>${violation.html.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
                            </div>
                        ` : ''}
                    </div>
                    <div class="rule-info">
                        <h4>💡 How to Fix This Issue</h4>
                        <div class="rule-description">
                            ${getRuleDescription(violation.id || 'general')}
                        </div>
                    </div>
                </div>
              `).join('')}
        </div>

        <div class="footer">
            <p>Report generated by <strong>Nibyor ADA Checker</strong> • ${safeTimestamp}</p>
            <p>For detailed accessibility consulting, visit <a href="https://nibyor.com" target="_blank">nibyor.com</a></p>
        </div>
    </div>
</body>
</html>`;

  return template;
}

// Create directory for ADA scan results if it doesn't exist
async function createAdaDirectoryIfNotExists(path = 'ada-scans') {
  try {
    const dirResponse = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${path}`,
      {
        headers: {
          'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );

    if (!dirResponse.ok) {
      // Directory doesn't exist, create it with a README
      await fetch(
        `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${path}/README.md`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
            'Accept': 'application/vnd.github.v3+json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: 'Initialize ADA scan results directory',
            content: btoa('# ADA Scan Results\n\nThis directory contains ADA compliance scan results stored as JSON files.'),
            branch: 'main'
          })
        }
      );
    }
  } catch (error) {
    console.error('Error checking/creating ADA directory:', error);
  }
}

// Get existing file SHA for updates
async function getFileSha(path) {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${path}`,
      {
        headers: {
          'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );
    if (response.ok) {
      const data = await response.json();
      return data.sha;
    }
    return null;
  } catch (error) {
    console.error('Error getting file SHA:', error);
    return null;
  }
}

// Save ADA scan result to GitHub as JSON
export async function saveAdaScanToGitHub(scanResult) {
  try {
    if (!GITHUB_CONFIG.token) {
      throw new Error('GitHub token is not configured');
    }

    // Create a unique filename with timestamp and URL hash
    const timestamp = new Date().toISOString();
    const urlHash = btoa(scanResult.url).replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
    const baseFilename = `scan_${formatDate(new Date())}_${urlHash}_${Date.now()}`;
    const jsonPath = `ada-scans/${baseFilename}.json`;
    const htmlPath = `ada-scans/${baseFilename}.html`;

    // Prepare the scan data with additional metadata
    const scanData = {
      ...scanResult,
      savedAt: timestamp,
      filename: baseFilename,
      htmlReportPath: htmlPath,
      metadata: {
        version: '1.0',
        tool: 'Nibyor ADA Checker',
        savedBy: 'automated-system'
      }
    };

    // Generate HTML report
    const htmlReport = generateHTMLReport(scanData);

    // Ensure directory exists
    await createAdaDirectoryIfNotExists();

    // Save both JSON and HTML to GitHub
    const [jsonResponse, htmlResponse] = await Promise.all([
      // Save JSON file
      fetch(
        `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${jsonPath}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
            'Accept': 'application/vnd.github.v3+json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: `Add ADA scan result for ${new URL(scanResult.url).hostname} - ${formatDate(new Date())}`,
            content: btoa(unescape(encodeURIComponent(JSON.stringify(scanData, null, 2)))),
            branch: 'main'
          })
        }
      ),
      // Save HTML file
      fetch(
        `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${htmlPath}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
            'Accept': 'application/vnd.github.v3+json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: `Add ADA HTML report for ${new URL(scanResult.url).hostname} - ${formatDate(new Date())}`,
            content: btoa(unescape(encodeURIComponent(htmlReport))),
            branch: 'main'
          })
        }
      )
    ]);

    if (!jsonResponse.ok || !htmlResponse.ok) {
      const jsonError = !jsonResponse.ok ? await jsonResponse.json() : null;
      const htmlError = !htmlResponse.ok ? await htmlResponse.json() : null;
      throw new Error(`GitHub API Error: ${jsonError?.message || htmlError?.message || 'Unknown error'}`);
    }

    const jsonResult = await jsonResponse.json();
    const htmlResult = await htmlResponse.json();
    
    console.log('ADA scan saved to GitHub:', jsonResult.content.name, 'and', htmlResult.content.name);
    
    return {
      success: true,
      githubPath: jsonPath,
      htmlPath: htmlPath,
      downloadUrl: jsonResult.content.download_url,
      htmlDownloadUrl: htmlResult.content.download_url,
      filename: baseFilename
    };

  } catch (error) {
    console.error('Error saving ADA scan to GitHub:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

// Save daily summary of all scans
export async function saveAdaDailySummary(scans) {
  try {
    if (!GITHUB_CONFIG.token) {
      throw new Error('GitHub token is not configured');
    }

    const date = formatDate(new Date());
    const summaryPath = `ada-scans/daily-summaries/${date}.json`;
    
    // Create summary data
    const summary = {
      date: date,
      totalScans: scans.length,
      scansWithIssues: scans.filter(scan => scan.totalIssues > 0).length,
      scansWithoutIssues: scans.filter(scan => scan.totalIssues === 0).length,
      avgIssuesPerScan: scans.length > 0 ? scans.reduce((sum, scan) => sum + scan.totalIssues, 0) / scans.length : 0,
      uniqueDomains: [...new Set(scans.map(scan => {
        try {
          return new URL(scan.url).hostname;
        } catch {
          return scan.url;
        }
      }))],
      scans: scans.map(scan => ({
        url: scan.url,
        timestamp: scan.timestamp,
        totalIssues: scan.totalIssues,
        score: scan.score || (scan.violations ? Math.max(0, 100 - scan.violations.length * 5) : 0),
        githubFile: scan.githubFile || null
      }))
    };

    // Check if file already exists
    const existingSha = await getFileSha(summaryPath);

    // Ensure directory exists
    await createAdaDirectoryIfNotExists('ada-scans/daily-summaries');

    const requestBody = {
      message: `Daily ADA scan summary for ${date}`,
      content: btoa(unescape(encodeURIComponent(JSON.stringify(summary, null, 2)))),
      branch: 'main'
    };

    if (existingSha) {
      requestBody.sha = existingSha;
    }

    const response = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${summaryPath}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`GitHub API Error: ${errorData.message}`);
    }

    return { success: true };

  } catch (error) {
    console.error('Error saving daily summary to GitHub:', error);
    return { success: false, error: error.message };
  }
}

// Retrieve ADA scan results from GitHub
export async function getAdaScansFromGitHub(limit = 100) {
  try {
    if (!GITHUB_CONFIG.token) {
      throw new Error('GitHub token is not configured');
    }

    // Get list of files in ada-scans directory
    const response = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/ada-scans`,
      {
        headers: {
          'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );

    if (!response.ok) {
      return { success: false, data: [] };
    }

    const files = await response.json();
    const jsonFiles = files.filter(file => file.name.endsWith('.json') && file.name.startsWith('scan_'));
    
    // Sort by name (which includes timestamp) and limit results
    const sortedFiles = jsonFiles.sort((a, b) => b.name.localeCompare(a.name)).slice(0, limit);

    // Fetch content for each file
    const scans = await Promise.all(
      sortedFiles.map(async (file) => {
        try {
          const fileResponse = await fetch(file.download_url);
          const content = await fileResponse.text();
          return JSON.parse(content);
        } catch (error) {
          console.error(`Error fetching scan file ${file.name}:`, error);
          return null;
        }
      })
    );

    return {
      success: true,
      data: scans.filter(scan => scan !== null)
    };

  } catch (error) {
    console.error('Error retrieving ADA scans from GitHub:', error);
    return { success: false, data: [], error: error.message };
  }
}

// Retrieve HTML report from GitHub
export async function getHtmlReportFromGitHub(filename) {
  try {
    if (!GITHUB_CONFIG.token) {
      throw new Error('GitHub token is not configured');
    }

    // Construct HTML file path
    const htmlPath = `ada-scans/${filename}.html`;

    const response = await fetch(
      `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents/${htmlPath}`,
      {
        headers: {
          'Authorization': `Bearer ${GITHUB_CONFIG.token}`,
          'Accept': 'application/vnd.github.v3+json',
        }
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'HTML report not found' };
      }
      throw new Error(`GitHub API Error: ${response.status}`);
    }

    const fileData = await response.json();
    
    // Fetch the HTML content
    const htmlResponse = await fetch(fileData.download_url);
    const htmlContent = await htmlResponse.text();

    return {
      success: true,
      htmlContent: htmlContent,
      downloadUrl: fileData.download_url
    };

  } catch (error) {
    console.error('Error retrieving HTML report from GitHub:', error);
    return { success: false, error: error.message };
  }
}